import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faPercent,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../config/api';
import './SalaryYearGrid.css';

const MONTHS = [
  { num: 1, short: 'Jan' },
  { num: 2, short: 'Feb' },
  { num: 3, short: 'Mar' },
  { num: 4, short: 'Apr' },
  { num: 5, short: 'May' },
  { num: 6, short: 'Jun' },
  { num: 7, short: 'Jul' },
  { num: 8, short: 'Aug' },
  { num: 9, short: 'Sep' },
  { num: 10, short: 'Oct' },
  { num: 11, short: 'Nov' },
  { num: 12, short: 'Dec' },
];

const formatPounds = (value) => {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '—';
  return `£${n.toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const SalaryYearGrid = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [year, setYear] = useState(currentYear);
  const [users, setUsers] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [editor, setEditor] = useState(null); // { user, month, salary }
  const [amount, setAmount] = useState('');
  const [splitOverride, setSplitOverride] = useState('');
  const [showOverride, setShowOverride] = useState(false);

  const loadYear = useCallback(async (targetYear) => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, salariesRes] = await Promise.all([
        api.get('/users'),
        api.get(`/users/salaries/year/${targetYear}`),
      ]);

      if (!usersRes.data?.success) {
        throw new Error(usersRes.data?.message || 'Failed to load users');
      }
      if (!salariesRes.data?.success) {
        throw new Error(salariesRes.data?.message || 'Failed to load salaries');
      }

      const sortedUsers = [...(usersRes.data.data || [])].sort((a, b) =>
        (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')
      );
      setUsers(sortedUsers);
      setSalaries(salariesRes.data.data || []);
    } catch (err) {
      console.error('Salary year load failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load salary grid');
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadYear(year);
  }, [year, loadYear]);

  const salaryMap = useMemo(() => {
    const map = new Map();
    salaries.forEach((s) => {
      map.set(`${s.userId}:${s.month}`, s);
    });
    return map;
  }, [salaries]);

  const getCell = (userId, month) => salaryMap.get(`${userId}:${month}`) || null;

  const monthSplitHint = useCallback(
    (month) => {
      if (users.length !== 2) return null;
      const a = getCell(users[0].id, month);
      const b = getCell(users[1].id, month);
      if (!a || !b) return null;

      const override = [a, b].find(
        (s) => s.splitOverride !== null && s.splitOverride !== undefined
      );
      if (override) {
        const pct = parseFloat(override.splitOverride);
        const forUser0 = override.userId === users[0].id ? pct : 100 - pct;
        const forUser1 = 100 - forUser0;
        return {
          overridden: true,
          labels: [`${forUser0.toFixed(0)}%`, `${forUser1.toFixed(0)}%`],
        };
      }

      const total = parseFloat(a.amount) + parseFloat(b.amount);
      if (!(total > 0)) return null;
      const p0 = (parseFloat(a.amount) / total) * 100;
      const p1 = (parseFloat(b.amount) / total) * 100;
      return {
        overridden: false,
        labels: [`${p0.toFixed(0)}%`, `${p1.toFixed(0)}%`],
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, salaryMap]
  );

  const openEditor = (user, month) => {
    const existing = getCell(user.id, month);
    setEditor({ user, month, salary: existing });
    setAmount(existing ? String(parseFloat(existing.amount)) : '');
    setSplitOverride(
      existing && existing.splitOverride !== null && existing.splitOverride !== undefined
        ? String(existing.splitOverride)
        : ''
    );
    setShowOverride(
      Boolean(
        existing && existing.splitOverride !== null && existing.splitOverride !== undefined
      )
    );
    setSaveError(null);
  };

  const closeEditor = () => {
    if (saving) return;
    setEditor(null);
    setSaveError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editor) return;

    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setSaveError('Enter a salary amount greater than zero');
      return;
    }

    let overridePayload = null;
    if (showOverride && splitOverride !== '') {
      const o = parseInt(splitOverride, 10);
      if (Number.isNaN(o) || o < 0 || o > 100) {
        setSaveError('Split override must be 0–100, or leave blank');
        return;
      }
      overridePayload = o;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        userId: editor.user.id,
        month: editor.month,
        year,
        amount: parsedAmount,
      };
      // Only touch splitOverride when the advanced section is open
      if (showOverride) {
        payload.splitOverride = overridePayload;
      }

      const res = await api.post('/users/salary', payload);

      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Save failed');
      }

      const saved = res.data.data;
      setSalaries((prev) => {
        const without = prev.filter(
          (s) => !(s.userId === saved.userId && s.month === saved.month && s.year === saved.year)
        );
        return [...without, saved];
      });
      setEditor(null);
    } catch (err) {
      console.error('Salary save failed:', err);
      setSaveError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const monthLabel = (monthNum) =>
    MONTHS.find((m) => m.num === monthNum)?.short || String(monthNum);

  if (loading && users.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading salaries…</p>
      </div>
    );
  }

  return (
    <div className="salary-year-grid">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">Salaries</h1>
          <p className="text-muted mb-0 small">
            Click a cell to set or edit that person’s salary for the month.
            Splits for salary-based expenses use these amounts.
          </p>
        </div>
        <div className="salary-year-switcher d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            aria-label="Previous year"
            onClick={() => setYear((y) => y - 1)}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          <span className="salary-year-label fw-semibold">{year}</span>
          <Button
            variant="outline-secondary"
            size="sm"
            aria-label="Next year"
            onClick={() => setYear((y) => y + 1)}
            disabled={loading || year >= currentYear + 1}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
          {year !== currentYear && (
            <Button
              variant="link"
              size="sm"
              className="text-decoration-none"
              onClick={() => setYear(currentYear)}
            >
              This year
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
          <div className="mt-2">
            <Button size="sm" variant="outline-danger" onClick={() => loadYear(year)}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {users.length === 0 && !loading ? (
        <Alert variant="warning">No users found. Sign in and try again.</Alert>
      ) : (
        <div className="salary-grid-scroll card shadow-sm">
          {loading && (
            <div className="salary-grid-loading">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          <table className="salary-grid-table">
            <thead>
              <tr>
                <th className="salary-grid-user-col">Person</th>
                {MONTHS.map((m) => (
                  <th
                    key={m.num}
                    className={
                      year === currentYear && m.num === currentMonth
                        ? 'salary-grid-month-col is-current'
                        : 'salary-grid-month-col'
                    }
                  >
                    {m.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <th scope="row" className="salary-grid-user-col">
                    <div className="salary-grid-user-name">{user.displayName}</div>
                    <div className="salary-grid-user-email text-muted">{user.email}</div>
                  </th>
                  {MONTHS.map((m) => {
                    const cell = getCell(user.id, m.num);
                    const isCurrent =
                      year === currentYear && m.num === currentMonth;
                    return (
                      <td key={m.num} className={isCurrent ? 'is-current' : undefined}>
                        <button
                          type="button"
                          className={`salary-cell ${cell ? 'has-value' : 'is-empty'}`}
                          onClick={() => openEditor(user, m.num)}
                          title={
                            cell
                              ? `Edit ${user.displayName} — ${m.short} ${year}`
                              : `Set ${user.displayName} — ${m.short} ${year}`
                          }
                        >
                          {cell ? (
                            <>
                              <span className="salary-cell-amount">
                                {formatPounds(cell.amount)}
                              </span>
                              {cell.splitOverride !== null &&
                                cell.splitOverride !== undefined && (
                                  <Badge bg="info" pill className="salary-cell-override">
                                    <FontAwesomeIcon icon={faPercent} className="me-1" />
                                    {cell.splitOverride}
                                  </Badge>
                                )}
                            </>
                          ) : (
                            <span className="salary-cell-empty">
                              <FontAwesomeIcon icon={faPlus} className="me-1" />
                              Set
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {users.length === 2 && (
                <tr className="salary-grid-split-row">
                  <th scope="row" className="salary-grid-user-col text-muted">
                    Split hint
                  </th>
                  {MONTHS.map((m) => {
                    const hint = monthSplitHint(m.num);
                    return (
                      <td key={m.num}>
                        {hint ? (
                          <div
                            className={`salary-split-hint ${
                              hint.overridden ? 'is-override' : ''
                            }`}
                            title={
                              hint.overridden
                                ? 'Using split override'
                                : 'From salary ratio'
                            }
                          >
                            {hint.labels[0]} / {hint.labels[1]}
                          </div>
                        ) : (
                          <span className="text-muted">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="small text-muted mt-3 mb-0">
        Optional split override (per cell, advanced) replaces the salary-ratio % for
        that month. Leave it off to keep normal income-based splits.
      </p>

      <Modal show={Boolean(editor)} onHide={closeEditor} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editor?.salary ? 'Edit salary' : 'Set salary'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editor && (
              <>
                <p className="mb-3">
                  <strong>{editor.user.displayName}</strong>
                  <span className="text-muted">
                    {' '}
                    · {monthLabel(editor.month)} {year}
                  </span>
                </p>

                {saveError && <Alert variant="danger">{saveError}</Alert>}

                <Form.Group className="mb-3">
                  <Form.Label>Monthly salary (£)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    autoFocus
                    required
                    placeholder="e.g. 3200"
                  />
                </Form.Group>

                <Button
                  variant="link"
                  className="px-0 mb-2 text-decoration-none"
                  type="button"
                  onClick={() => setShowOverride((v) => !v)}
                >
                  {showOverride ? 'Hide' : 'Show'} advanced split override
                </Button>

                <Collapse in={showOverride}>
                  <div>
                    <Form.Group className="mb-2">
                      <Form.Label>
                        This person’s share of expenses (0–100%)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={splitOverride}
                        onChange={(e) => setSplitOverride(e.target.value)}
                        placeholder="Leave blank to clear override"
                      />
                      <Form.Text muted>
                        Optional. If set, the other person gets the remainder.
                        Clear the field and save to remove an override.
                      </Form.Text>
                    </Form.Group>
                  </div>
                </Collapse>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SalaryYearGrid;
